import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  CalendarDays,
  Loader2,
  Plus,
  RefreshCw,
  ShieldCheck,
  ShieldOff,
  User,
  X,
} from 'lucide-react';
import { Alert, AlertProps } from '../../../Shared/Utils/Alert';
import {
  getAccessGrants,
  getAccessStats,
  getDataConnections,
  getGrantableUsers,
  grantDataAccess,
  revokeDataAccess,
} from '../../Services/DataService';
import {
  AccessPermission,
  DataAccessGrant,
  DataAccessStats,
  DataConnection,
  GrantableUser,
  PERMISSION_OPTIONS,
} from '../../Types/DataServiceType';
import SearchableSelect, { SearchableOption } from './SearchableSelect';

const emptyStats: DataAccessStats = {
  totalActive: 0,
  readOnlyGrants: 0,
  readWriteGrants: 0,
  expiringSoon: 0,
};

const formatDate = (value?: string | null) => {
  if (!value) return 'No expiry';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No expiry';
  return date.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const StatCard = ({
  label,
  value,
  icon,
  accent,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  accent: string;
}) => (
  <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
    <div className="flex items-center justify-between">
      <span className={`flex h-9 w-9 items-center justify-center rounded-xl border ${accent}`}>{icon}</span>
      <span className="text-2xl font-black text-slate-100">{value}</span>
    </div>
    <p className="mt-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
  </div>
);

const AccessComponent = () => {
  const [connections, setConnections] = useState<DataConnection[]>([]);
  const [connectionId, setConnectionId] = useState('');
  const [grants, setGrants] = useState<DataAccessGrant[]>([]);
  const [stats, setStats] = useState<DataAccessStats>(emptyStats);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [users, setUsers] = useState<GrantableUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userId, setUserId] = useState('');
  const [tablePattern, setTablePattern] = useState('');
  const [permission, setPermission] = useState<AccessPermission>('READ');
  const [expiresAt, setExpiresAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [revokeTarget, setRevokeTarget] = useState<DataAccessGrant | null>(null);
  const [revoking, setRevoking] = useState(false);

  const selectedConnection = connections.find((item) => item.id === connectionId) ?? null;
  const isReadOnlyConnection = selectedConnection?.defaultPermission === 'READ';

  useEffect(() => {
    if (!isReadOnlyConnection) return;
    // The server caps a write grant at the connection ceiling, so do not offer
    // an option that would only be silently downgraded.
    setPermission('READ');
  }, [isReadOnlyConnection]);

  const loadConnections = useCallback(async () => {
    try {
      const response = await getDataConnections({}, 0, 100);
      if (response.success && response.data) {
        const items = response.data.page?.content ?? [];
        setConnections(items);
        setConnectionId((current) => (current && items.some((i) => i.id === current) ? current : items[0]?.id ?? ''));
      }
    } catch (err) {
      console.error('Failed to load connections', err);
    }
  }, []);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  const loadGrants = useCallback(async () => {
    if (!connectionId) {
      setGrants([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const [grantResponse, statsResponse] = await Promise.all([
        getAccessGrants({ connectionId }),
        getAccessStats(),
      ]);

      if (grantResponse.success) {
        setGrants(grantResponse.data ?? []);
      } else {
        setGrants([]);
        setError(grantResponse.message || 'Could not load the grants on this connection.');
      }

      if (statsResponse.success && statsResponse.data) {
        setStats(statsResponse.data);
      }
    } catch (err) {
      console.error('Failed to load grants', err);
      setGrants([]);
      setError('An unexpected error occurred while loading the grants.');
    } finally {
      setLoading(false);
    }
  }, [connectionId]);

  useEffect(() => {
    loadGrants();
  }, [loadGrants]);

  const loadUsers = useCallback(async (search = '') => {
    setLoadingUsers(true);
    try {
      const response = await getGrantableUsers(search);
      if (response.success && response.data) {
        setUsers(response.data.page?.content ?? []);
      }
    } catch (err) {
      console.error('Failed to load users', err);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    if (modalOpen) {
      setUserId('');
      setTablePattern('');
      setPermission('READ');
      setExpiresAt('');
      setFormError('');
      // The user list is not fetched here: the picker requests the unfiltered
      // page itself when it is first opened, so no request is spent on a modal
      // the user may not reach the picker in.
    }
  }, [modalOpen]);

  const userOptions = useMemo<SearchableOption[]>(
    () =>
      users.map((user: GrantableUser) => ({
        value: user.id,
        label: user.firstName || user.lastName ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : user.email,
        hint: user.role,
        // The name is the label, so the address still has to be matchable.
        keywords: `${user.email} ${user.role} ${user.status ?? ''}`,
      })),
    [users]
  );

  const handleGrant = async () => {
    if (!userId) {
      setFormError('Pick the user who should get access.');
      return;
    }
    if (!connectionId) {
      setFormError('Pick a connection.');
      return;
    }
    if (tablePattern.trim() && !/^[\w.%]+$/.test(tablePattern.trim())) {
      setFormError('A table pattern may only contain letters, digits, "_", "." and the % wildcard.');
      return;
    }
    if (expiresAt && new Date(expiresAt).getTime() <= Date.now()) {
      setFormError('The expiry must be in the future.');
      return;
    }

    setSaving(true);
    setFormError('');

    try {
      const response = await grantDataAccess({
        userId,
        connectionId,
        tablePattern: tablePattern.trim() || undefined,
        permission,
        // The backend field is a LocalDateTime, so send the local ISO form
        // (yyyy-MM-ddTHH:mm:ss). A UTC string with a trailing Z is rejected.
        expiresAt: expiresAt ? expiresAt.length === 16 ? `${expiresAt}:00` : expiresAt : undefined,
      });

      if (response.success) {
        setAlert({
          variant: 'success',
          title: 'Granted',
          message: 'The grant is active immediately.',
          autoCloseMs: 4000,
          onClose: () => setAlert(null),
        });
        setModalOpen(false);
        loadGrants();
      } else {
        setFormError(response.message || 'The grant could not be created.');
      }
    } catch (err) {
      console.error('Failed to create grant', err);
      setFormError('The grant could not be created.');
    } finally {
      setSaving(false);
    }
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      const response = await revokeDataAccess(revokeTarget.id);
      if (response.success) {
        setAlert({
          variant: 'success',
          title: 'Revoked',
          message: 'The grant no longer grants anything.',
          autoCloseMs: 4000,
          onClose: () => setAlert(null),
        });
        setRevokeTarget(null);
        loadGrants();
      } else {
        setAlert({
          variant: 'error',
          title: 'Revoke failed',
          message: response.message || 'The grant could not be revoked.',
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      }
    } finally {
      setRevoking(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      {alert && <Alert {...alert} />}

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
            <ShieldCheck size={22} />
          </span>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">Delegated Access</h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">
              Grant delegates access to a connection, capped by its own permission ceiling
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={loadGrants}
            disabled={loading}
            className="flex items-center justify-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40 disabled:opacity-50"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button
            onClick={() => setModalOpen(true)}
            disabled={!connectionId}
            className="flex items-center justify-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400 disabled:opacity-50"
          >
            <Plus size={15} /> Grant access
          </button>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Active grants"
          value={stats.totalActive}
          icon={<ShieldCheck size={16} />}
          accent="border-cyan-500/30 bg-cyan-500/10 text-cyan-400"
        />
        <StatCard
          label="Read only"
          value={stats.readOnlyGrants}
          icon={<ShieldOff size={16} />}
          accent="border-sky-500/30 bg-sky-500/10 text-sky-400"
        />
        <StatCard
          label="Read / write"
          value={stats.readWriteGrants}
          icon={<ShieldCheck size={16} />}
          accent="border-amber-500/30 bg-amber-500/10 text-amber-400"
        />
        <StatCard
          label="Expiring soon"
          value={stats.expiringSoon}
          icon={<CalendarDays size={16} />}
          accent="border-rose-500/30 bg-rose-500/10 text-rose-400"
        />
      </div>

      <div className="mb-6 rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-400">
          Connection
        </label>
        <select
          value={connectionId}
          onChange={(e) => setConnectionId(e.target.value)}
          className="w-full appearance-none rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-200 outline-none transition [color-scheme:dark] focus:border-cyan-500/50"
        >
          <option value="" className="bg-slate-900 text-slate-100">
            Select a connection
          </option>
          {connections.map((item) => (
            <option key={item.id} value={item.id} className="bg-slate-900 text-slate-100">
              {item.alias} ({item.environment}) · {item.defaultPermission === 'READ' ? 'read only' : 'read / write'}
            </option>
          ))}
        </select>

        {isReadOnlyConnection && (
          <p className="mt-2 flex items-center gap-1.5 text-[11px] font-bold text-amber-400">
            <AlertTriangle size={12} /> This connection is read-only, so every grant on it is read-only
            regardless of what is requested.
          </p>
        )}
      </div>

      {loading ? (
        <div className="space-y-4">
          {[0, 1].map((idx) => (
            <div key={idx} className="h-20 animate-pulse rounded-2xl border border-slate-800 bg-slate-900/40" />
          ))}
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-rose-500/20 bg-rose-500/5 py-16 text-center text-sm font-bold text-rose-400">
          {error}
        </div>
      ) : grants.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/30 py-20 text-center">
          <ShieldCheck size={26} className="mx-auto mb-2 text-slate-600" />
          <p className="text-xs font-black uppercase tracking-widest text-slate-400">No grants on this connection</p>
          <p className="mt-1 text-[11px] font-semibold text-slate-500">
            Only you can reach it right now.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {grants.map((grant) => (
            <div
              key={grant.id}
              className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 transition hover:border-slate-700"
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="flex items-center gap-1.5 text-sm font-black text-slate-100">
                      <User size={14} className="text-slate-500" />
                      {grant.userEmail || grant.userId}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${
                        grant.effectivePermission === 'READ_WRITE'
                          ? 'border-amber-500/30 bg-amber-500/10 text-amber-400'
                          : 'border-sky-500/30 bg-sky-500/10 text-sky-400'
                      }`}
                    >
                      {grant.effectivePermission === 'READ_WRITE' ? 'Read / write' : 'Read only'}
                    </span>
                    {grant.expired && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-rose-500/30 bg-rose-500/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-rose-400">
                        Expired
                      </span>
                    )}
                    {grant.connectionIsReadOnly && grant.permission === 'READ_WRITE' && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-amber-400">
                        Capped to read
                      </span>
                    )}
                  </div>

                  <p className="mt-1.5 text-[11px] font-bold text-slate-500">
                    Tables:{' '}
                    <span className="text-slate-300">
                      {grant.tablePattern || 'the whole connection'}
                    </span>{' '}
                    · Expires: {formatDate(grant.expiresAt)}
                    {grant.grantedByName ? ` · by ${grant.grantedByName}` : ''}
                  </p>
                </div>

                <button
                  onClick={() => setRevokeTarget(grant)}
                  className="flex items-center justify-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-rose-400 transition hover:border-rose-500/40"
                >
                  <ShieldOff size={12} /> Revoke
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Grant modal */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/70 p-4 backdrop-blur-sm"
            onClick={() => !saving && setModalOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              onClick={(e) => e.stopPropagation()}
              className="my-8 w-full max-w-xl rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl"
            >
              <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
                    <ShieldCheck size={18} />
                  </span>
                  <div>
                    <h2 className="text-base font-black tracking-tight text-slate-100">Grant access</h2>
                    <p className="text-[11px] font-semibold text-slate-500">
                      {selectedConnection?.alias}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setModalOpen(false)}
                  disabled={saving}
                  className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-slate-200"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="space-y-4 px-6 py-5">
                {formError && (
                  <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-xs font-bold text-rose-300">
                    {formError}
                  </div>
                )}

                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-400">
                    User *
                  </label>
                  <SearchableSelect
                    options={userOptions}
                    value={userId}
                    onChange={setUserId}
                    onSearch={loadUsers}
                    loading={loadingUsers}
                    placeholder="Search by name or email…"
                    emptyLabel="Select a user"
                  />
                  <p className="mt-1.5 text-[10px] font-semibold text-slate-500">
                    Searches every user on the platform, not just the current page.
                  </p>
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-400">
                    Table pattern
                  </label>
                  <input
                    type="text"
                    value={tablePattern}
                    onChange={(e) => setTablePattern(e.target.value)}
                    placeholder="Leave blank for the whole connection, or use % as a wildcard"
                    className="w-full rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-200 placeholder-slate-600 outline-none transition focus:border-cyan-500/50"
                  />
                  <p className="mt-1.5 text-[10px] font-semibold text-slate-500">
                    The most specific matching pattern wins. A literal table name beats a wildcard.
                  </p>
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-400">
                    Permission *
                  </label>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {PERMISSION_OPTIONS.map((option) => {
                      const blocked = option.value === 'READ_WRITE' && isReadOnlyConnection;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          disabled={blocked}
                          onClick={() => setPermission(option.value)}
                          className={`flex items-center gap-2.5 rounded-xl border px-3.5 py-2.5 text-xs font-bold transition disabled:cursor-not-allowed disabled:opacity-40 ${
                            permission === option.value
                              ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-300'
                              : 'border-slate-800 bg-slate-950 text-slate-400 hover:border-slate-700'
                          }`}
                        >
                          {option.value === 'READ' ? <ShieldOff size={14} /> : <ShieldCheck size={14} />}
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-1.5 text-[10px] font-semibold text-slate-500">
                    A delegated admin is always downgraded to read only, whatever is selected here.
                  </p>
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-400">
                    Expires
                  </label>
                  <input
                    type="datetime-local"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    className="w-full rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-200 outline-none transition [color-scheme:dark] focus:border-cyan-500/50"
                  />
                  <p className="mt-1.5 text-[10px] font-semibold text-slate-500">
                    Leave blank for a grant that does not expire on its own.
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 border-t border-slate-800 px-6 py-4">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  disabled={saving}
                  className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleGrant}
                  disabled={saving}
                  className="flex items-center gap-2 rounded-xl bg-cyan-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400 disabled:opacity-50"
                >
                  {saving ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />}
                  {saving ? 'Granting' : 'Grant access'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {revokeTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
          onClick={() => !revoking && setRevokeTarget(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl"
          >
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-400">
                <AlertTriangle size={20} />
              </span>
              <div>
                <h3 className="text-base font-black tracking-tight text-slate-100">Revoke this grant?</h3>
                <p className="mt-1 text-sm text-slate-400">
                  <span className="font-bold text-slate-200">{revokeTarget.userEmail}</span> will lose
                  access to {revokeTarget.tablePattern || 'the whole connection'}. The record is kept
                  for the audit trail.
                </p>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => setRevokeTarget(null)}
                disabled={revoking}
                className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmRevoke}
                disabled={revoking}
                className="flex items-center gap-2 rounded-xl bg-rose-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-white transition hover:bg-rose-400 disabled:opacity-50"
              >
                {revoking ? <Loader2 size={15} className="animate-spin" /> : <ShieldOff size={15} />}
                {revoking ? 'Revoking' : 'Revoke'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AccessComponent;
